/**
 * WEB COPY of LetterTierExplainerScreen.tsx — picked by the web bundler ahead
 * of the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Same tiers, prices and copy. The phone buys through the app store; the
 * web buys through Stripe Checkout (decision D4). App Store pricing is
 * never mentioned here. When LetterTierExplainerScreen.tsx changes,
 * re-apply the change here.
 */
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { billingService } from '../../services/billingService';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ms, s } from '../../utils/responsive';


type ExplainerTier = 'basic' | 'plus' | 'premium';

type ExplainerRouteProp = RouteProp<{ params: { tier: ExplainerTier } }, 'params'>;

const TIERS: Record<
  ExplainerTier,
  {
    name: string;
    headline: string;
    sub: string;
    adds: string[];
    yearly: string;
    monthly: string;
  }
> = {
  basic: {
    name: 'Basic',
    headline: 'Your information vault',
    sub: 'Personal, medical, finances, property — the details your family will need, organized while you can still explain them.',
    adds: [
      'The full information vault, with Orion to guide you',
      'Unlimited data entry and secure cloud backup',
      'Share access with your successors and keyholders',
    ],
    yearly: '$24.99',
    monthly: '$2.99',
  },
  plus: {
    name: 'Plus',
    headline: 'Video messages, alongside your vault',
    sub: 'Everything in Basic, plus the recordings that only your face can carry.',
    adds: [
      'Everything in Basic',
      'Video messages for the people you love (arriving soon)',
      '500MB document storage on your device',
    ],
    yearly: '$59.99',
    monthly: '$6.99',
  },
  premium: {
    name: 'Premium',
    headline: 'Your documents, kept and organized',
    sub: 'Everything in Plus, with room for 2GB of important papers, encrypted on your phone and the web.',
    adds: [
      'Everything in Plus',
      '2GB for documents on your phone and the web — wills, directives, deeds, policies',
      'Priority support and early access to new features',
    ],
    yearly: '$99.99',
    monthly: '$9.99',
  },
};

export const LetterTierExplainerScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<ExplainerRouteProp>();
  const tier = route.params?.tier ?? 'basic';
  const config = TIERS[tier];
  const { currentTier } = useSubscription();

  const hasDelivery = ['letter', 'basic', 'plus', 'premium'].includes(currentTier);

  const [isAnnual, setIsAnnual] = useState(true);

  // Stripe hosts the checkout page; the browser goes there and comes back
  // to the Billing screen. A tier without a web price says so.
  const [starting, setStarting] = useState(false);
  const handleUpgrade = async () => {
    if (starting) return;
    try {
      setStarting(true);
      await billingService.startCheckout(tier, isAnnual ? 'yearly' : 'monthly');
    } catch (error: any) {
      setStarting(false);
      Alert.alert(
        'Checkout',
        error?.response?.data?.message ||
          'Web checkout is not available for this plan right now. You can also choose it in the FinalKey app on your phone.',
      );
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{config.name}</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <Text style={styles.title}>{config.headline}</Text>
          <Text style={styles.subtitle}>{config.sub}</Text>

          <View style={styles.addsCard}>
            {config.adds.map((line) => (
              <View key={line} style={styles.addRow}>
                <Image
                  source={require('../../assets/icons/check.png')}
                  style={styles.addIcon}
                />
                <Text style={styles.addText}>{line}</Text>
              </View>
            ))}
          </View>

          {!hasDelivery && (
            <View style={styles.deliveryNote}>
              <Text style={styles.deliveryNoteText}>
                Letter delivery (the $9.99 plan) is of course included — you
                can finish that setup any time.
              </Text>
            </View>
          )}

          <TouchableOpacity
            style={[styles.planCard, isAnnual && styles.planCardSelected]}
            onPress={() => setIsAnnual(true)}
            accessibilityRole="button"
            accessibilityLabel={`Annual plan, ${config.yearly} per year`}
            accessibilityState={{ selected: isAnnual }}
          >
            <View style={styles.planHeader}>
              <Text style={styles.planName}>Annual</Text>
              <View style={styles.bestValue}>
                <Text style={styles.bestValueText}>Best value</Text>
              </View>
            </View>
            <Text style={styles.planPrice}>{config.yearly}/year</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.planCard, !isAnnual && styles.planCardSelected]}
            onPress={() => setIsAnnual(false)}
            accessibilityRole="button"
            accessibilityLabel={`Monthly plan, ${config.monthly} per month`}
            accessibilityState={{ selected: !isAnnual }}
          >
            <Text style={styles.planName}>Monthly</Text>
            <Text style={styles.planPrice}>{config.monthly}/month</Text>
            <Text style={styles.planHint}>Cancel any time</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.upgradeButton, starting && styles.dim]}
            onPress={handleUpgrade}
            disabled={starting}
            accessibilityRole="button"
            accessibilityLabel={`Upgrade to ${config.name}`}
          >
            <Text style={styles.upgradeButtonText}>
              Upgrade now — {isAnnual ? `${config.yearly}/year` : `${config.monthly}/month`}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.laterButton}
            onPress={() => navigation.goBack()}
            accessibilityRole="button"
            accessibilityLabel="Not today"
          >
            <Text style={styles.laterButtonText}>Not today</Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(24),
    paddingHorizontal: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: '#6B7280',
    marginBottom: ms(16),
  },
  addsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(12),
  },
  addRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: ms(10),
    marginBottom: ms(8),
  },
  addIcon: {
    width: s(18),
    height: s(18),
    tintColor: '#29BFC0',
    marginTop: ms(2),
  },
  addText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  deliveryNote: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#C7E9EB',
    padding: ms(14),
    marginBottom: ms(12),
  },
  deliveryNoteText: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#1C2C4F',
  },
  planCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(2),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(10),
  },
  planCardSelected: {
    borderColor: '#29BFC0',
    backgroundColor: '#F0FBFB',
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  planName: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  bestValue: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(12),
    paddingHorizontal: ms(10),
    paddingVertical: ms(4),
  },
  bestValueText: {
    color: '#FFFFFF',
    fontSize: ms(11, 0.3),
    fontWeight: '600',
  },
  planPrice: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
    marginTop: ms(4),
  },
  planHint: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  upgradeButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
    marginTop: ms(6),
  },
  upgradeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  laterButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
  },
  laterButtonText: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#6B7280',
  },
  bottomPadding: {
    height: s(40),
  },
  dim: {
    opacity: 0.6,
  },
});

export default LetterTierExplainerScreen;
